import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useChatStore } from '../store/chatStore';
import { useAuthStore } from '@shared/store/authStore';
import { sendChatMessage, getWelcomeMessage } from '../utils/aiChat';
import { ChatMessage } from '../components/chat/ChatMessage';
import { TypingIndicator } from '../components/chat/TypingIndicator';
import { useVoiceChat } from '../hooks/useVoiceChat';

// Нормализация чисел: "седьмое января" → "7 января"
function normalizeNumbers(text: string): string {
  if (!text) return text;
  const ordinals: Record<string, string> = {
    'первое': '1', 'первого': '1', 'первый': '1', 'второе': '2', 'второго': '2', 'второй': '2',
    'третье': '3', 'третьего': '3', 'третий': '3', 'четвертое': '4', 'четвертого': '4', 'четвертый': '4',
    'пятое': '5', 'пятого': '5', 'пятый': '5', 'шестое': '6', 'шестого': '6', 'шестой': '6',
    'седьмое': '7', 'седьмого': '7', 'седьмой': '7', 'восьмое': '8', 'восьмого': '8', 'восьмой': '8',
    'девятое': '9', 'девятого': '9', 'девятый': '9', 'десятое': '10', 'десятого': '10', 'десятый': '10',
    'одиннадцатое': '11', 'одиннадцатого': '11', 'одиннадцатый': '11',
    'двенадцатое': '12', 'двенадцатого': '12', 'двенадцатый': '12',
    'тринадцатое': '13', 'тринадцатого': '13', 'тринадцатый': '13',
    'четырнадцатое': '14', 'четырнадцатого': '14', 'четырнадцатый': '14',
    'пятнадцатое': '15', 'пятнадцатого': '15', 'пятнадцатый': '15',
    'шестнадцатое': '16', 'шестнадцатого': '16', 'шестнадцатый': '16',
    'семнадцатое': '17', 'семнадцатого': '17', 'семнадцатый': '17',
    'восемнадцатое': '18', 'восемнадцатого': '18', 'восемнадцатый': '18',
    'девятнадцатое': '19', 'девятнадцатого': '19', 'девятнадцатый': '19',
    'двадцатое': '20', 'двадцатого': '20', 'двадцатый': '20',
    'двадцать первое': '21', 'двадцать первого': '21', 'двадцать первый': '21',
    'двадцать второе': '22', 'двадцать второго': '22', 'двадцать второй': '22',
    'двадцать третье': '23', 'двадцать третьего': '23', 'двадцать третий': '23',
    'двадцать четвертое': '24', 'двадцать четвертого': '24', 'двадцать четвертый': '24',
    'двадцать пятое': '25', 'двадцать пятого': '25', 'двадцать пятый': '25',
    'двадцать шестое': '26', 'двадцать шестого': '26', 'двадцать шестой': '26',
    'двадцать седьмое': '27', 'двадцать седьмого': '27', 'двадцать седьмой': '27',
    'двадцать восьмое': '28', 'двадцать восьмого': '28', 'двадцать восьмой': '28',
    'двадцать девятое': '29', 'двадцать девятого': '29', 'двадцать девятый': '29',
    'тридцатое': '30', 'тридцатого': '30', 'тридцатый': '30',
    'тридцать первое': '31', 'тридцать первого': '31', 'тридцать первый': '31'
  };
  let result = text.toLowerCase();
  Object.keys(ordinals).sort((a, b) => b.length - a.length).forEach(word => {
    result = result.replace(new RegExp('(^|\\s)' + word + '($|\\s|[,.])', 'gi'), '$1' + ordinals[word] + '$2');
  });
  // Убираем запятые и "и" перед месяцами: "7, января" → "7 января", "7 и января" → "7 января"
  result = result.replace(/(\d+)[,\s]*(и\s+)?(?=янва|февра|марта|апрел|мая|июня|июля|авгус|сентя|октяб|ноябр|декаб)/gi, '$1 ');

  // Время словами: "с девяти до десяти" → "с 9 до 10"
  const timeWords: Record<string, string> = {
    'час': '1', 'одного': '1', 'двух': '2', 'трёх': '3', 'трех': '3', 'четырёх': '4', 'четырех': '4',
    'пяти': '5', 'шести': '6', 'семи': '7', 'восьми': '8', 'девяти': '9', 'десяти': '10',
    'одиннадцати': '11', 'двенадцати': '12', 'тринадцати': '13', 'четырнадцати': '14',
    'пятнадцати': '15', 'шестнадцати': '16', 'семнадцати': '17', 'восемнадцати': '18',
    'девятнадцати': '19', 'двадцати': '20', 'двадцати одного': '21', 'двадцати двух': '22', 'двадцати трёх': '23'
  };
  Object.keys(timeWords).sort((a, b) => b.length - a.length).forEach(word => {
    result = result.replace(new RegExp('(с|до|в|на|к)\\s+' + word, 'gi'), '$1 ' + timeWords[word]);
  });

  return result;
}

function AIAssistantPage() {
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const { messages, isTyping, addMessage, setTyping, loadMessagesFromStorage } = useChatStore();
  const [inputValue, setInputValue] = useState('');
  const [isInitialized, setIsInitialized] = useState(false);
  const [recordingError, setRecordingError] = useState<string | null>(null); // ✅ FIX #7: Состояние для ошибок
  const [isProcessingClick, setIsProcessingClick] = useState(false); // ✅ FIX #6: Защита от двойного клика
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Voice chat hook (HTTP STT via Whisper)
  const {
    isRecording,
    isProcessing,
    error: voiceError,
    startRecording,
    stopRecording
  } = useVoiceChat(user?.id, {
    onTranscript: async (rawText) => {
      // Нормализуем числа перед отображением
      const text = normalizeNumbers(rawText);
      if (text && text.trim()) {
        addMessage({ role: 'user', content: text });
        setTyping(true);

        try {
          // Передаём history для FSM контекста
          const history = messages.map(m => ({ role: m.role, content: m.content }));
          const response = await sendChatMessage(text, user?.id, history);
          if (response.reply && response.reply.trim()) {
            addMessage({ role: 'assistant', content: response.reply });
          }
        } catch (error) {
          console.error('[Voice] Chat error:', error);
          addMessage({ role: 'assistant', content: 'Произошла ошибка. Попробуйте еще раз.' });
        } finally {
          setTyping(false);
        }
      }
    }
  });

  const isConnecting = false; // Не нужен для HTTP
  const micInterrupted = false; // Не нужен для HTTP

  // ✅ FIX #4 & #6: Обработка ошибок и защита от двойного клика
  const toggleRecording = async () => {
    // Защита от двойного нажатия
    if (isProcessingClick) {
      console.log('[Voice] Already processing click, ignoring');
      return;
    }

    try {
      setIsProcessingClick(true);
      setRecordingError(null); // Сбрасываем предыдущую ошибку

      if (isRecording) {
        await stopRecording();
      } else {
        await startRecording(); // ✅ Добавили await
      }
    } catch (error) {
      // Обрабатываем ошибку
      const errorMessage = error instanceof Error
        ? error.message
        : 'Не удалось начать запись';

      setRecordingError(errorMessage);
      console.error('[Voice] Recording error:', error);
    } finally {
      setIsProcessingClick(false); // Освобождаем кнопку
    }
  };

  // Auto-scroll to bottom
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages, isTyping]);

  // Load history and welcome message on mount
  useEffect(() => {
    const loadInitialData = async () => {
      loadMessagesFromStorage();

      // Проверяем количество сообщений после загрузки из storage
      // Используем setTimeout чтобы дождаться обновления state
      setTimeout(() => {
        // Берём актуальное значение из store, а не из замыкания
        const currentMessages = useChatStore.getState().messages;
        if (currentMessages.length === 0 && !isInitialized) {
          setIsInitialized(true);
          loadWelcomeMessage();
        }
      }, 100);
    };

    loadInitialData();

    // Listen for suggested messages
    const handleSuggestedMessage = (event: any) => {
      const message = event.detail.message;
      setInputValue(message);
      inputRef.current?.focus();
    };

    window.addEventListener('send-suggested-message', handleSuggestedMessage);
    return () => {
      window.removeEventListener('send-suggested-message', handleSuggestedMessage);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Запускается только один раз при монтировании

  const loadWelcomeMessage = async () => {
    setTyping(true);
    try {
      const response = await getWelcomeMessage(user?.id);
      if (response.success) {
        addMessage({
          role: 'assistant',
          content: response.reply,
          suggestions: response.suggestions
        });
      }
    } finally {
      setTyping(false);
    }
  };

  const handleSend = async () => {
    if (!inputValue.trim() || isTyping) return;

    const userMessage = inputValue.trim();
    setInputValue('');

    // Add user message
    addMessage({
      role: 'user',
      content: userMessage
    });

    // Show typing indicator
    setTyping(true);

    try {
      // Send to AI
      const history = messages.map(m => ({
        role: m.role,
        content: m.content
      }));

      const response = await sendChatMessage(userMessage, user?.id, history);

      // Add assistant response
      const replyText = response.reply || '';
      if (response.success && replyText.trim()) {
        addMessage({
          role: 'assistant',
          content: replyText,
          suggestions: response.suggestions
        });
      } else if (!response.success) {
        addMessage({
          role: 'assistant',
          content: replyText.trim() || 'Извините, произошла ошибка. Попробуйте снова.'
        });
      }
    } finally {
      setTyping(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <div className="flex flex-col h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-4 py-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <button
              onClick={() => navigate(-1)}
              className="text-gray-600 hover:text-gray-900"
            >
              ← Назад
            </button>
            <div>
              <h1 className="text-lg font-bold text-gray-900">AI Помощник</h1>
              <p className="text-xs text-gray-500">Задайте любой вопрос</p>
            </div>
          </div>
          <div className="text-2xl">🤖</div>
        </div>
      </div>

      {/* Messages Container */}
      <div className="flex-1 overflow-y-auto px-4 py-6 space-y-4">
        {/* ✅ FIX #7: Error Banner */}
        {recordingError && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
            <div className="flex items-start justify-between">
              <div className="flex items-start space-x-3">
                <svg className="w-6 h-6 text-red-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <div className="flex-1">
                  <p className="text-sm font-medium text-red-800">Ошибка записи</p>
                  <p className="text-xs text-red-600 mt-1">{recordingError}</p>
                </div>
              </div>
              <button
                onClick={() => setRecordingError(null)}
                className="text-red-400 hover:text-red-600"
              >
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
              </button>
            </div>
          </div>
        )}

        {/* Voice Error Banner */}
        {voiceError && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
            <div className="flex items-center space-x-3">
              <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
              <p className="text-sm font-medium text-red-800">{voiceError}</p>
            </div>
          </div>
        )}

        {messages.map((message) => (
          <ChatMessage key={message.id} message={message} />
        ))}

        {isTyping && <TypingIndicator />}

        <div ref={messagesEndRef} />
      </div>

      {/* Input Container */}
      <div className="bg-white border-t border-gray-200 px-4 py-3">
        <div className="flex items-center gap-2">
          {/* Microphone button - слева от поля ввода */}
          <button
            onClick={toggleRecording}
            disabled={isConnecting || isProcessingClick}
            className={`flex-shrink-0 p-3 rounded-full transition-all ${
              isRecording
                ? 'bg-red-500 hover:bg-red-600'
                : isConnecting || isProcessingClick
                ? 'bg-yellow-500'
                : 'bg-emerald-500 hover:bg-emerald-600'
            } text-white disabled:bg-gray-300 disabled:cursor-not-allowed`}
            title={isRecording ? 'Остановить запись' : 'Начать голосовую запись'}
          >
            {(isConnecting || isProcessingClick) ? (
              <svg className="w-5 h-5 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none"/>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"/>
              </svg>
            ) : isRecording ? (
              <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                <path d="M6 6l12 12M18 6L6 18"/>
              </svg>
            ) : (
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                <rect x="2" y="8" width="2" height="8" rx="1"/>
                <rect x="6" y="5" width="2" height="14" rx="1"/>
                <rect x="10" y="2" width="2" height="20" rx="1"/>
                <rect x="14" y="6" width="2" height="12" rx="1"/>
                <rect x="18" y="9" width="2" height="6" rx="1"/>
              </svg>
            )}
          </button>

          {/* Input field */}
          <div className="flex-1 relative">
            <input
              ref={inputRef}
              type="text"
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="Напишите сообщение..."
              disabled={isTyping}
              className="w-full px-4 py-3 pr-12 border border-gray-300 rounded-full focus:outline-none focus:ring-2 focus:ring-emerald-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
            />

            {/* Send button - показывается только когда есть текст */}
            {inputValue.trim() && (
              <button
                onClick={handleSend}
                disabled={isTyping}
                className="absolute right-2 top-1/2 -translate-y-1/2 p-2 text-emerald-500 hover:text-emerald-600 disabled:text-gray-300 disabled:cursor-not-allowed transition-all"
                title="Отправить"
              >
                <svg className="w-6 h-6 -rotate-90" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z"/>
                </svg>
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default AIAssistantPage;
export { AIAssistantPage };
